import { getVeloConstant } from '../../react/shared/lib/veloConstants'
import { trackEvent } from './eventTracker'
import { showSnackbar } from './snackbar'

// Must stay in sync with the like_button component in
// lib/veloplanner_ex_web/components/blog_components/like_button.ex.
const LIKED_ICON_CLASSES = ['hero-heart-solid', 'text-red-600']
const NOT_LIKED_ICON_CLASSES = ['hero-heart', 'text-gray-500']

const BOUNCE_CLASS = 'velo-animate-bounce-once'
const LOGIN_DIALOG_ID = 'blog-like-login-dialog'

// Per post rather than per button: a post has a button at the top and one at
// the end, and both send the same request.
const busyPostIds = new Set<string>()

type SaveResult =
  | { status: 'saved'; likesCount: number | null }
  | { status: 'unauthorized' }
  | { status: 'failed' }

/**
 * The server-rendered like buttons of a blog post (and the login dialog
 * guests get instead). `root` is for tests.
 */
export function initBlogLikeButtons(root: Document = document) {
  root.addEventListener('click', function handleBlogLikeClick(event) {
    if (!(event.target instanceof Element)) {
      return
    }

    const button = event.target.closest<HTMLElement>('[data-blog-like-button]')
    if (!button) {
      return
    }

    void toggleBlogLike(button, root)
  })

  initLoginDialog(root)
}

// The × closes it natively (form method="dialog"), Esc too.
function initLoginDialog(root: Document) {
  const dialog = root.getElementById(LOGIN_DIALOG_ID) as HTMLDialogElement | null
  if (!dialog) {
    return
  }

  dialog.addEventListener('click', function closeOnBackdropClick(event) {
    // The dialog has no padding, so only a click on the backdrop targets it.
    if (event.target === dialog) {
      dialog.close()
    }
  })
}

async function toggleBlogLike(clickedButton: HTMLElement, root: Document) {
  if (!getVeloConstant('isUserLoggedIn', false)) {
    askGuestToLogIn(root)
    return
  }

  const postId = clickedButton.dataset.postId
  const likeUrl = clickedButton.dataset.likeUrl
  if (!postId || !likeUrl || busyPostIds.has(postId)) {
    return
  }

  const buttons = findPostButtons(root, postId)
  const wasLiked = clickedButton.dataset.liked === 'true'
  const previousCount = Number(clickedButton.querySelector('[data-blog-like-count]')?.textContent)
  const liked = !wasLiked

  busyPostIds.add(postId)
  setButtonsState(buttons, liked, liked ? previousCount + 1 : Math.max(0, previousCount - 1))

  if (liked) {
    bounceIcons(buttons)
  }

  const result = await saveLike(likeUrl, liked)
  busyPostIds.delete(postId)

  if (result.status === 'saved') {
    // Without a readable count the optimistic one stays: the like itself is saved.
    if (result.likesCount !== null) {
      setButtonsState(buttons, liked, result.likesCount)
    }

    trackEvent(liked ? 'blog_post__like' : 'blog_post__unlike', { post_id: Number(postId) })
    return
  }

  setButtonsState(buttons, wasLiked, previousCount)

  if (result.status === 'unauthorized') {
    // The session expired after the page was rendered.
    redirectToLogin()
    return
  }

  showSnackbar('error', clickedButton.dataset.errorMessage || 'Something went wrong')
}

async function saveLike(likeUrl: string, liked: boolean): Promise<SaveResult> {
  try {
    const response = await fetch(likeUrl, {
      method: liked ? 'POST' : 'DELETE',
      headers: {
        'X-CSRF-Token': getVeloConstant('csrfToken'),
      },
    })

    if (response.status === 401) {
      return { status: 'unauthorized' }
    }

    if (!response.ok) {
      return { status: 'failed' }
    }

    const body = await response.json().catch(function ignoreUnreadableBody() {
      return null
    })
    const likesCount = typeof body?.likes_count === 'number' ? body.likes_count : null

    return { status: 'saved', likesCount }
  } catch {
    return { status: 'failed' }
  }
}

function askGuestToLogIn(root: Document) {
  const dialog = root.getElementById(LOGIN_DIALOG_ID) as HTMLDialogElement | null
  if (!dialog) {
    redirectToLogin()
    return
  }

  dialog.showModal()
}

function redirectToLogin() {
  const loginPath = getVeloConstant('routes.login')
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.assign(`${loginPath}?return_to=${returnTo}`)
}

function findPostButtons(root: Document, postId: string) {
  const buttons = Array.from(root.querySelectorAll<HTMLElement>('[data-blog-like-button]'))

  return buttons.filter(function isPostButton(button) {
    return button.dataset.postId === postId
  })
}

function setButtonsState(buttons: HTMLElement[], liked: boolean, count: number) {
  buttons.forEach(function setButtonState(button) {
    button.dataset.liked = String(liked)
    button.setAttribute('aria-pressed', String(liked))

    const icon = button.querySelector<HTMLElement>('[data-blog-like-icon]')
    if (icon) {
      icon.classList.remove(...(liked ? NOT_LIKED_ICON_CLASSES : LIKED_ICON_CLASSES))
      icon.classList.add(...(liked ? LIKED_ICON_CLASSES : NOT_LIKED_ICON_CLASSES))
    }

    const countElement = button.querySelector<HTMLElement>('[data-blog-like-count]')
    if (countElement) {
      countElement.textContent = String(count)
    }
  })
}

function bounceIcons(buttons: HTMLElement[]) {
  buttons.forEach(function bounceIcon(button) {
    const icon = button.querySelector<HTMLElement>('[data-blog-like-icon]')
    if (!icon) {
      return
    }

    // The reflow between removing and adding restarts the animation, so a
    // like right after an unlike still bounces.
    icon.classList.remove(BOUNCE_CLASS)
    void icon.offsetWidth
    icon.classList.add(BOUNCE_CLASS)
  })
}
